import _ from 'underscore';
import BaseStore, { type Dispatcher } from 'vendor/cnpm/fluxible.v0-4/addons/BaseStore';

import user from 'js/lib/user';

import CourseRoles from 'bundles/common/constants/CourseRoles';
import { legacyHydrate } from 'bundles/discussions/utils/hydrate';
import type { HydratedNaptimeElement, NaptimeLinkedResponse } from 'bundles/discussions/utils/hydrate';
import type { OnDemandClassmateProfilesV1 } from 'bundles/naptimejs/resources/__generated__/OnDemandClassmateProfilesV1';

type CourseMaterials = $TSFixMe; // CourseMaterials based on pages/open-course/common/models/courseMaterials

const SERIALIZED_PROPS: Array<keyof ClassmatesProfileStore$DehydratedState> = [
  'profiles',
  'learnerCount',
  'instructorIds',
  'staffSocialProfiles',
];

type CourseRole = keyof typeof CourseRoles;

// The generated type wraps these two in objects, but the REST response returns plain numbers.
type ClassmateProfileElement = Omit<OnDemandClassmateProfilesV1, 'courseProgressPercentage' | 'userId'> & {
  courseProgressPercentage: number;
  userId: number;
};

type LinkedSocialProfile = {
  courseRole?: CourseRole;
  fullName?: string;
  photoUrl?: string;
};

type ClassmateProfileLinked = {
  'onDemandSocialProfiles.v1': LinkedSocialProfile;
  'forumHelpers.v1': { helperStatus?: string };
};

type ClassmateProfilesResponse = NaptimeLinkedResponse<ClassmateProfileElement, ClassmateProfileLinked>;

type Profile = HydratedNaptimeElement<ClassmateProfileElement, ClassmateProfileLinked>;

export type NormalizedProfile = Profile & LinkedSocialProfile & { externalId: string; countryCode?: number };

// Declared locally: the generated `OnDemandSocialProfilesV1` types `userId` as `{ id }` and omits `helperStatus`.
export type StaffSocialProfile = {
  courseId: string;
  courseRole: CourseRole;
  externalUserId: string;
  fullName: string;
  id: string;
  learnerId: number;
  userId: number;
  photoUrl?: string;
  helperStatus?: string;
};

type StaffSocialProfilesResponse = {
  elements?: Array<StaffSocialProfile> | null;
};

type ClassmatesProfileStore$DehydratedState = {
  profiles: Array<NormalizedProfile>;
  learnerCount: number;
  instructorIds: Array<string>;
  staffSocialProfiles: Array<StaffSocialProfile>;
};

const normalizeProfiles = (profiles: Array<Profile>): Array<NormalizedProfile> => {
  return profiles.map((profile) => {
    return {
      ...profile,
      externalId: profile.id,
      ...profile['onDemandSocialProfiles.v1'],
    };
  });
};

class ClassmatesProfileStore extends BaseStore {
  static storeName = 'ClassmatesProfileStore';

  declare profiles: Array<NormalizedProfile>;

  declare learnerCount: number;

  declare instructorIds: Array<string>;

  declare staffSocialProfiles: Array<StaffSocialProfile>;

  declare profilesRequested: Array<string>;

  declare emitChange: () => void;

  static handlers = {
    LOAD_CLASSMATES_PROFILES: 'onLoadClassmatesProfiles',
    LOAD_LEARNER_COUNT: 'onLoadLearnerCount',
    LOAD_STAFF_SOCIAL_PROFILES: 'onLoadStaffSocialProfiles',
    LOAD_COURSE_MATERIALS: 'onLoadCourseMaterials',
    PROFILE_REQUESTED: 'onProfileRequested',
  };

  // The API has dispatched responses without `elements` (Sentry ONDEMAND-25KG).
  onLoadClassmatesProfiles(response: ClassmateProfilesResponse | { elements?: undefined } | undefined) {
    const newProfiles = response?.elements ? normalizeProfiles(legacyHydrate(response)) : [];

    this.profiles = _(newProfiles.concat(this.profiles)).uniq((profile) => profile.userId);
    this.emitChange();
  }

  onLoadLearnerCount(count: number) {
    this.learnerCount = count;
    this.emitChange();
  }

  onLoadStaffSocialProfiles(response: StaffSocialProfilesResponse | undefined) {
    this.staffSocialProfiles = response?.elements ?? [];
    this.combineSocialProfilesAndInstructors();
    this.emitChange();
  }

  onLoadCourseMaterials({ courseMaterials }: { courseMaterials: CourseMaterials }) {
    const course = courseMaterials.get('course');
    this.instructorIds = course.get('instructors').map((instructor: $TSFixMe) => instructor.get('id'));

    this.combineSocialProfilesAndInstructors();
    this.emitChange();
  }

  onProfileRequested(externalId: string) {
    if (!this.profilesRequested.includes(externalId)) {
      this.profilesRequested.push(externalId);
    }

    this.emitChange();
  }

  constructor(dispatcher: Dispatcher) {
    super(dispatcher);

    this.profiles = [];
    this.learnerCount = 0;
    this.instructorIds = [];
    this.staffSocialProfiles = [];
    this.profilesRequested = [];
  }

  dehydrate(): ClassmatesProfileStore$DehydratedState {
    return _(this).pick(...SERIALIZED_PROPS);
  }

  rehydrate(state: ClassmatesProfileStore$DehydratedState) {
    Object.assign(this, _(state).pick(...SERIALIZED_PROPS));
  }

  getStaffProfiles(): Array<StaffSocialProfile> {
    return (this.staffSocialProfiles ?? []).slice();
  }

  get10Profiles(countryCode: number): Array<NormalizedProfile> {
    return _(this.profiles)
      .chain()
      .filter((profile) => {
        // Filter out the user's own profile if they are logged in, since the intention is to fetch classmates.
        if (user.isAuthenticatedUser() && profile.userId === user.get().id) {
          return false;
        } else if (countryCode && profile.countryCode !== countryCode) {
          return false;
        } else {
          return true;
        }
      })
      .first(10)
      .value();
  }

  /*
   * Called whenever staff social profile or instructors are updated. Instructors could have a higher CourseRole than
   * instructor, such as UNIVERSITY_ADMIN. For the purposes of this store, we don't care about that information but
   * instead want to accurately tell whether a user is an instructor or not. Therefore overwite courseRole with
   * INSTRUCTOR when the user is in the course instructors object from the course API.
   */
  combineSocialProfilesAndInstructors() {
    this.staffSocialProfiles = this.staffSocialProfiles.map((staffSocialProfile) => {
      const isInstructor =
        staffSocialProfile.courseRole === CourseRoles.INSTRUCTOR ||
        _(this.instructorIds).contains(staffSocialProfile.userId + '');

      return Object.assign(staffSocialProfile, {
        courseRole: isInstructor ? CourseRoles.INSTRUCTOR : staffSocialProfile.courseRole,
      });
    });
  }
}

export default ClassmatesProfileStore;
